import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/toaster';

// Pages
import LandingPage from './pages/LandingPage';
import RegisterPage from './pages/RegisterPage';
import GuestLoginPage from './pages/GuestLoginPage';
import AdminLoginPage from './pages/AdminLoginPage';
import LivePage from './pages/LivePage';
import PasswordSetupPage from './pages/PasswordSetupPage';

// Portal
import PortalLayout from './layouts/PortalLayout';
import PortalDashboard from './pages/portal/Dashboard';
import ProfilePage from './pages/portal/ProfilePage';
import BriefingPage from './pages/portal/BriefingPage';
import AccommodationPage from './pages/portal/AccommodationPage';
import SupportPage from './pages/portal/Personalehåndbog';
import BarPage from './pages/portal/BarPage';
import MusicPage from './pages/portal/MusicPage';
import WishesPage from './pages/portal/WishesPage';
import PhotosPage from './pages/portal/PhotosPage';
import ChatPage from './pages/portal/ChatPage';
import EntertainmentPage from './pages/portal/EntertainmentPage';
import GuestDirectoryPage from './pages/portal/GuestDirectoryPage';

// Admin
import AdminLayout from './layouts/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminInvitations from './pages/admin/AdminInvitations';
import AdminGuests from './pages/admin/AdminGuests';
import AdminRSVP from './pages/admin/AdminRSVP';
import AdminEmails from './pages/admin/AdminEmails';
import AdminMusic from './pages/admin/AdminMusic';
import AdminExport from './pages/admin/AdminExport';
import AdminTables from './pages/admin/AdminTables';
import AdminCheckin from './pages/admin/AdminCheckin';
import AdminImages from './pages/admin/AdminImages';
import AdminWishes from './pages/admin/AdminWishes';
import AdminDrinks from './pages/admin/AdminDrinks';
import AdminAudit from './pages/admin/AdminAudit';
import SlideshowPage from './pages/admin/SlideshowPage';
import AdminAI from './pages/admin/AdminAI';
import AdminContent from './pages/admin/AdminContent';

function GuestRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user || user.role === 'admin') return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user || user.role !== 'admin') return <Navigate to="/admin/login" replace />;
  return children;
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-slate-400">Indlæser...</p>
      </div>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/register/:code" element={<RegisterPage />} />
      <Route path="/login" element={<GuestLoginPage />} />
      <Route path="/password-setup" element={<PasswordSetupPage />} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/live" element={<LivePage />} />
      <Route path="/slideshow" element={<SlideshowPage publicMode />} />
      <Route path="/admin/slideshow" element={<AdminRoute><SlideshowPage /></AdminRoute>} />

      {/* Guest Portal */}
      <Route path="/portal" element={<GuestRoute><PortalLayout /></GuestRoute>}>
        <Route index element={<PortalDashboard />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="briefing" element={<BriefingPage />} />
        <Route path="accommodation" element={<AccommodationPage />} />
        <Route path="support" element={<SupportPage />} />
        <Route path="music" element={<MusicPage />} />
        <Route path="bar" element={<BarPage />} />
        <Route path="wishes" element={<WishesPage />} />
        <Route path="photos" element={<PhotosPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="entertainment" element={<EntertainmentPage />} />
        <Route path="directory" element={<GuestDirectoryPage />} />
      </Route>

      {/* Admin */}
      <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="invitations" element={<AdminInvitations />} />
        <Route path="guests" element={<AdminGuests />} />
        <Route path="rsvp" element={<AdminRSVP />} />
        <Route path="checkin" element={<AdminCheckin />} />
        <Route path="tables" element={<AdminTables />} />
        <Route path="emails" element={<AdminEmails />} />
        <Route path="music" element={<AdminMusic />} />
        <Route path="images" element={<AdminImages />} />
        <Route path="wishes" element={<AdminWishes />} />
        <Route path="drinks" element={<AdminDrinks />} />
        <Route path="audit" element={<AdminAudit />} />
        <Route path="content" element={<AdminContent />} />
        <Route path="ai" element={<AdminAI />} />
        <Route path="export" element={<AdminExport />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
